import React, { useEffect, useState, useMemo } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { METHOD_COLORS, SOURCE_COLORS, MethodBadge } from './utils/colorConfig';

// Register the required chart components
ChartJS.register(ArcElement, Tooltip, Legend, Title);

function LocationPieChart({ data }) {
  const location = useLocation();
  const navigate = useNavigate();

  // Get filters from URL parameters or localStorage with priority to URL
  const getInitialFilterState = () => {
    const searchParams = new URLSearchParams(location.search);
    const defaults = {
      displayMode: 'amount',
      selectedMethod: 'all',
      selectedSource: 'all',
      groupBy: 'source'
    };

    // Check URL parameters first
    const urlDisplayMode = searchParams.get('display');
    const urlMethod = searchParams.get('method');
    const urlSource = searchParams.get('source');
    const urlGroupBy = searchParams.get('groupBy');

    // Check localStorage as fallback
    const storedDisplayMode = localStorage.getItem('tipl_analytics_displayMode');
    const storedMethod = localStorage.getItem('tipl_analytics_method');
    const storedSource = localStorage.getItem('tipl_analytics_source');
    const storedGroupBy = localStorage.getItem('tipl_analytics_groupBy');

    // Prioritize URL parameters over localStorage, then defaults
    const result = {
      displayMode: urlDisplayMode || storedDisplayMode || defaults.displayMode,
      selectedMethod: urlMethod || storedMethod || defaults.selectedMethod,
      selectedSource: urlSource || storedSource || defaults.selectedSource,
      groupBy: urlGroupBy || storedGroupBy || defaults.groupBy
    };

    // Update URL if we used localStorage values and they differ from URL
    if (!urlDisplayMode && storedDisplayMode && storedDisplayMode !== defaults.displayMode ||
      !urlMethod && storedMethod && storedMethod !== defaults.selectedMethod ||
      !urlSource && storedSource && storedSource !== defaults.selectedSource ||
      !urlGroupBy && storedGroupBy && storedGroupBy !== defaults.groupBy) {
      setTimeout(() => {
        updateURLFromState(result);
      }, 0);
    }

    return result;
  };

  const initialState = getInitialFilterState();
  const [chartData, setChartData] = useState(null);
  const [displayMode, setDisplayMode] = useState(initialState.displayMode); // 'amount' or 'count'
  const [selectedMethod, setSelectedMethod] = useState(initialState.selectedMethod); // 'all', 'page', 'button', 'qrCode'
  const [selectedSource, setSelectedSource] = useState(initialState.selectedSource); // 'all' or specific URL
  const [groupBy, setGroupBy] = useState(initialState.groupBy); // 'source' or 'method'

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024); // Tailwind's lg breakpoint

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to update URL from current filter state
  const updateURLFromState = (state) => {
    const params = new URLSearchParams(location.search);
    if (state.displayMode !== 'amount') params.set('display', state.displayMode);
    else params.delete('display');

    if (state.selectedMethod !== 'all') params.set('method', state.selectedMethod);
    else params.delete('method');

    if (state.selectedSource !== 'all') params.set('source', state.selectedSource);
    else params.delete('source');

    if (state.groupBy !== 'source') params.set('groupBy', state.groupBy);
    else params.delete('groupBy');

    // Only include non-default parameters to keep URL clean
    const queryString = params.toString();
    const newPath = queryString ? `${location.pathname}?${queryString}` : location.pathname;

    // Avoid redundant navigations that can cause render loops
    const currentPath = location.pathname + location.search;
    if (newPath === currentPath) return;

    // Update URL without refreshing page
    navigate(newPath, { replace: true });
  };

  // Update both URL and localStorage when filters change
  useEffect(() => {
    // Update URL
    updateURLFromState({
      displayMode,
      selectedMethod,
      selectedSource,
      groupBy
    });

    // Update localStorage
    localStorage.setItem('tipl_analytics_displayMode', displayMode);
    localStorage.setItem('tipl_analytics_method', selectedMethod);
    localStorage.setItem('tipl_analytics_source', selectedSource);
    localStorage.setItem('tipl_analytics_groupBy', groupBy);
  }, [displayMode, selectedMethod, selectedSource, groupBy]);

  // Auto-adjust groupBy based on selected filters
  useEffect(() => {
    if (selectedSource !== 'all' && selectedMethod === 'all') {
      setGroupBy('method');
    } else if (selectedMethod !== 'all' && selectedSource === 'all') {
      setGroupBy('source') // When filtering by both, default to source
    }
  }, [selectedMethod, selectedSource]);

  // Determine effective grouping mode based on user's selection and current filters
  // const evaluateGroupBy = useMemo(() => {
  //   if (groupByMode !== 'auto') {
  //     return groupByMode; // User explicitly selected source or method
  //   }

  //   // Auto mode logic
  //   if (selectedMethod !== 'all' && selectedSource === 'all') {
  //     return 'source'; // When filtering by method, group by source
  //   } else if (selectedSource !== 'all' && selectedMethod === 'all') {
  //     return 'method'; // When filtering by source, group by method
  //   } else if (selectedMethod !== 'all' && selectedSource !== 'all') {
  //     return 'source'; // When filtering by both, default to source
  //   } else {
  //     return 'source'; // Default when both are 'all'
  //   }
  // }, [groupByMode, selectedMethod, selectedSource]);

  // Process the data based on the current filters and group by mode
  const filteredData = useMemo(() => {
    // If the data is not in the expected format or isn't loaded yet
    if (!data || !data.detailedBreakdown) return [];

    console.log('detailedBreakdown: ', data.detailedBreakdown);

    // Handle the data structure
    let dataToProcess = [];

    // When both filters are set to specific values
    if (selectedMethod !== 'all' && selectedSource !== 'all') {
      // For specific method and source, just show that data point
      dataToProcess = data.detailedBreakdown.filter(item =>
        item.method === selectedMethod &&
        item.normalizedUrl === selectedSource
      );
    }
    // When filtering by method only
    else if (selectedMethod !== 'all' && selectedSource === 'all') {
      if (groupBy === 'source') {
        // Group by source for this method
        // First filter by the selected method
        const filteredItems = data.detailedBreakdown.filter(item => item.method === selectedMethod);
        
        // Then group by normalizedUrl
        dataToProcess = Object.values(filteredItems.reduce((acc, item) => {
          const key = item.normalizedUrl;
          if (!acc[key]) {
            acc[key] = {
              ...item,
              totalAmount: 0,
              count: 0
            };
          }
          acc[key].totalAmount += item.totalAmount;
          acc[key].count += item.count;
          return acc;
        }, {}));
      } else {
        // For method-specific view but grouped by method (unusual but allowed)
        dataToProcess = [{
          normalizedUrl: 'all',
          method: selectedMethod,
          totalAmount: data.detailedBreakdown
            .filter(item => item.method === selectedMethod)
            .reduce((sum, item) => sum + item.totalAmount, 0),
          count: data.detailedBreakdown
            .filter(item => item.method === selectedMethod)
            .reduce((sum, item) => sum + item.count, 0),
          currency: data.detailedBreakdown[0]?.currency || 'USD'
        }];
      }
    }
    // When filtering by source only
    else if (selectedSource !== 'all' && selectedMethod === 'all') {
      if (groupBy === 'method') {
        // Group by method for this source
        // First filter by the selected source
        const filteredItems = data.detailedBreakdown.filter(item => item.normalizedUrl === selectedSource);
        
        // Then group by method
        dataToProcess = Object.values(filteredItems.reduce((acc, item) => {
          const key = item.method;
          if (!acc[key]) {
            acc[key] = {
              ...item,
              totalAmount: 0,
              count: 0
            };
          }
          acc[key].totalAmount += item.totalAmount;
          acc[key].count += item.count;
          return acc;
        }, {}));
      } else {
        // For source-specific view but grouped by source (unusual but allowed)
        dataToProcess = [{
          normalizedUrl: selectedSource,
          method: 'all',
          totalAmount: data.detailedBreakdown
            .filter(item => item.normalizedUrl === selectedSource)
            .reduce((sum, item) => sum + item.totalAmount, 0),
          count: data.detailedBreakdown
            .filter(item => item.normalizedUrl === selectedSource)
            .reduce((sum, item) => sum + item.count, 0),
          // Get the correct currency by finding the first matching item with this source
          currency: data.detailedBreakdown.find(item => item.normalizedUrl === selectedSource)?.currency || data.detailedBreakdown[0]?.currency || 'USD'
        }];
      }
    }
    // No filters - group by user preference
    else {
      if (groupBy === 'source') {
        // Group by source
        dataToProcess = Object.values(data.detailedBreakdown.reduce((acc, item) => {
          const key = item.normalizedUrl;
          if (!acc[key]) {
            acc[key] = {
              url: item.url,
              title: item.title,
              domain: item.domain,
              favicon: item.favicon,
              normalizedUrl: item.normalizedUrl,
              totalAmount: 0,
              count: 0,
              currency: item.currency
            };
          }
          acc[key].totalAmount += item.totalAmount;
          acc[key].count += item.count;
          return acc;
        }, {}));
      } else {
        // Group by method
        dataToProcess = Object.values(data.detailedBreakdown.reduce((acc, item) => {
          const key = item.method;
          if (!acc[key]) {
            acc[key] = {
              method: item.method,
              totalAmount: 0,
              count: 0,
              currency: item.currency
            };
          }
          acc[key].totalAmount += item.totalAmount;
          acc[key].count += item.count;
          return acc;
        }, {}));
      }
    }

    // For backward compatibility with old data format
    if (dataToProcess.length === 0 && Array.isArray(data)) {
      dataToProcess = [...data]; // Create a copy to avoid mutations
    }

    // Ensure we always return an array
    return Array.isArray(dataToProcess) ? dataToProcess : [];
  }, [data, selectedMethod, selectedSource, groupBy]);

  // Get available sources for dropdown
  const availableSources = useMemo(() => {
    if (!data || !data.detailedBreakdown || !Array.isArray(data.detailedBreakdown)) return [];
    
    // Group by normalizedUrl to get unique sources
    const uniqueSources = {};
    data.detailedBreakdown.forEach(item => {
      if (!uniqueSources[item.normalizedUrl]) {
        uniqueSources[item.normalizedUrl] = {
          value: item.normalizedUrl,
          label: item.title || item.domain || item.url
        };
      }
    });
    
    return Object.values(uniqueSources);
  }, [data]);

  // We'll calculate chart data in the useEffect below

  useEffect(() => {
    if (filteredData.length === 0) {
      setChartData(null);
      return;
    }

    // Ensure we have a valid currency for each item in filteredData
    // This will be used as fallback if individual items don't have currency
    const defaultCurrency = filteredData.find(item => item?.currency)?.currency || 'USD';

    // Generate appropriate labels based on grouping mode
    const labels = filteredData.map(item => {
      console.log('printing filteredData.items: ', item);
      if (groupBy === 'method') {
        // For method grouping, use method names from our centralized configuration
        return METHOD_COLORS[item.method] ?
          METHOD_COLORS[item.method].label :
          (item.method ? item.method.charAt(0).toUpperCase() + item.method.slice(1) : 'Unknown');
      } else {
        // For source grouping, use title/domain/url
        return item.title || item.domain || item.url || item.normalizedUrl;
      }
    });

    const values = filteredData.map(item => displayMode === 'amount' ? item.totalAmount : item.count);

    // Generate background colors
    const allBackgroundColors = filteredData.map((item, index) => {
      // If we're grouping by method and the item has a method property,
      // use the corresponding method color
      if (groupBy === 'method' && item.method && METHOD_COLORS[item.method]) {
        return METHOD_COLORS[item.method].background;
      }

      // For other cases (sources or unknown methods), use the source colors
      return SOURCE_COLORS[index % SOURCE_COLORS.length];
    });

    setChartData({
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: allBackgroundColors,
          borderColor: allBackgroundColors.map(color => color),
          borderWidth: 1,
          // Store original data items for tooltip access
          originalData: filteredData
        },
      ],
    });
  }, [filteredData, displayMode, groupBy, data]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    font: {
      family: "'Baloo 2', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
    },
    layout: {
      padding: {
        left: 10,
        right: 10,
        top: 10,
        bottom: 10
      },
      // Explicitly set the chart area to a fixed percentage
      autoPadding: false
    },
    plugins: {
      legend: {
        position: isDesktop ? 'chartArea' : 'bottom',
        align: 'center',
        labels: {
          boxWidth: 15,
          padding: 20,
          font: {
            family: "'Baloo 2', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: `Tips by ${groupBy === 'source' ? 'source' : 'method'}`,
        font: {
          family: "'Baloo 2', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
          size: 24,
          color: 'black'
        }
      },
      tooltip: {
        titleFont: {
          family: "'Baloo 2', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
        },
        bodyFont: {
          family: "'Baloo 2', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
        },
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.raw || 0;
            const dataset = context.dataset;
            const total = dataset.data.reduce((acc, data) => acc + data, 0);
            const percentage = ((value / total) * 100).toFixed(1);

            // Get the original data item from filtered data
            const item = filteredData[context.dataIndex];

            if (displayMode === 'amount') {
              // Get currency from the item or use the first valid currency in filteredData
              const currency = item?.currency || filteredData.find(d => d?.currency)?.currency || 'USD';
              const formattedAmount = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency,
                minimumFractionDigits: 2
              }).format(value / 100); // Assuming amount is in cents

              return `${label}: ${formattedAmount} (${percentage}%)`;
            } else {
              // For count mode, just show the count and percentage
              return `${label}: ${value} tips (${percentage}%)`;
            }
          }
        }
      }
    }
  }), [isDesktop, groupBy, displayMode, filteredData]);

  // Display a message if there's no data
  // Check for both old (array) and new (object) formats
  if (!data ||
    (Array.isArray(data) && data.length === 0) ||
    (!Array.isArray(data) && (!data.byUrl || data.byUrl.length === 0))) {
    return (
      <div className="text-center py-10 text-gray-500">
        <p>No tip data available yet. Once you start receiving tips, you'll see analytics here.</p>
      </div>
    );
  }

  // Display a loading message while chart data is being prepared
  if (!chartData && filteredData.length > 0) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  console.log('filtered data: ', filteredData)
  console.log('filteredData is array: ', Array.isArray(filteredData))

  // Display a message if filtered data is empty
  // if (filteredData.length === 0) {
  //   return (
  //     <div>
  //       <div className="mb-6">
  //         <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
  //           <div className="flex flex-col space-y-4">
  //             {/* Controls (copy from main return) */}
  //             <div className="flex items-center justify-between">
  //               <p className="text-sm text-gray-600">
  //                 This chart shows where your tips are coming from. Hover over each segment for details.
  //               </p>
  //             </div>

  //             <div className="flex flex-wrap items-center gap-4">
  //               {/* Display toggle */}
  //               <div className="flex items-center">
  //                 <span className="text-sm text-gray-600 mr-2">Display by:</span>
  //                 <div className="inline-flex items-center bg-gray-200 rounded-full p-1 w-fit">
  //                   <button
  //                     className={`px-3 py-1 text-sm rounded-full transition-colors ${displayMode === 'amount' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
  //                     onClick={() => setDisplayMode('amount')}
  //                   >
  //                     Amount
  //                   </button>
  //                   <button
  //                     className={`px-3 py-1 text-sm rounded-full transition-colors ${displayMode === 'count' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
  //                     onClick={() => setDisplayMode('count')}
  //                   >
  //                     Count
  //                   </button>
  //                 </div>
  //               </div>

  //               {/* Group By toggle */}
  //               <div className="flex items-center">
  //                 <span className="text-sm text-gray-600 mr-2">Group by:</span>
  //                 <div className="inline-flex items-center bg-gray-200 rounded-full p-1 w-fit">
  //                   <button
  //                     className={`px-3 py-1 text-sm rounded-full transition-colors ${groupBy === 'source' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
  //                     onClick={() => setGroupBy('source')}
  //                   >
  //                     Source
  //                   </button>
  //                   {/* <button
  //                     className={`px-3 py-1 text-sm rounded-full transition-colors ${groupBy === 'auto' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
  //                     onClick={() => setGroupBy('auto')}
  //                   >
  //                     Auto
  //                   </button> */}
  //                   <button
  //                     className={`px-3 py-1 text-sm rounded-full transition-colors ${groupBy === 'method' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
  //                     onClick={() => setGroupBy('method')}
  //                   >
  //                     Method
  //                   </button>
  //                 </div>
  //               </div>



  //               {/* Method filter */}
  //               <div className="flex items-center">
  //                 <span className="text-sm text-gray-600 mr-2">Method:</span>
  //                 <select
  //                   className="bg-white border border-gray-300 rounded-md text-sm py-1 px-2"
  //                   value={selectedMethod}
  //                   onChange={(e) => setSelectedMethod(e.target.value)}
  //                 >
  //                   <option value="all">All Methods</option>
  //                   <option value="page">Page</option>
  //                   <option value="button">Button</option>
  //                   <option value="qrCode">QR Code</option>
  //                 </select>
  //               </div>

  //               {/* Source filter */}
  //               <div className="flex items-center">
  //                 <span className="text-sm text-gray-600 mr-2">Source:</span>
  //                 <select
  //                   className="bg-white border border-gray-300 rounded-md text-sm py-1 px-2 max-w-xs"
  //                   value={selectedSource}
  //                   onChange={(e) => setSelectedSource(e.target.value)}
  //                 >
  //                   <option value="all">All Sources</option>
  //                   {availableSources.map((source, index) => (
  //                     <option key={index} value={source.value}>
  //                       {source.label}
  //                     </option>
  //                   ))}
  //                 </select>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>


  //     </div>
  //   );
  // }

  return (
    <div role="region" aria-labelledby="chart-title">
      <h2 id="chart-title" className="sr-only">Tip Data Analytics</h2>
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="w-full lg:w-2/3">
          {(filteredData.length === 0) ?
            (<div className="py-10 text-center border-2 border-dashed border-gray-200 rounded-lg" role="alert">
              <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true">
                <title>No data icon</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No matching tip data</h3>
              <p className="mt-1 text-sm text-gray-500">
                No tips match the current filter combination.
              </p>
            </div>) : (
              <div role="figure" aria-label={`Pie chart showing tip data grouped by ${groupBy} and displaying by ${displayMode}`}>
                <div className="h-96 relative font-sans">
                  <div className="h-full w-full" style={{ position: 'relative' }}>
                    <Pie data={chartData} options={options} />
                  </div>
                </div>
              </div>
            )
          }
        </div>

        <div className="w-full lg:w-1/3 flex items-center">
          <div className="flex flex-col space-y-6 bg-gray-50 p-4 rounded-lg border border-gray-200 items-center">
            <div className="grid grid-cols-3 gap-y-4">
              {/* Group by row */}
              <div className="text-right pr-4 flex items-center justify-end h-full">
                <label className="text-sm text-gray-600 font-medium">Group by:</label>
              </div>
              <div className="col-span-2">
                <div className="inline-flex items-center bg-gray-200 rounded-full p-1 w-fit" role="group" aria-label="Group by">
                  <button
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${groupBy === 'source' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setGroupBy('source')}
                    aria-pressed={groupBy === 'source'}
                  >
                    Source
                  </button>
                  <button
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${groupBy === 'method' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setGroupBy('method')}
                    aria-pressed={groupBy === 'method'}
                  >
                    Method
                  </button>
                </div>
              </div>
              
              {/* Calculate by row */}
              <div className="text-right pr-4 flex items-center justify-end h-full">
                <label className="text-sm text-gray-600 font-medium">Display by:</label>
              </div>
              <div className="col-span-2">
                <div className="inline-flex items-center bg-gray-200 rounded-full p-1 w-fit" role="group" aria-label="Display by">
                  <button
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${displayMode === 'amount' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setDisplayMode('amount')}
                    aria-pressed={displayMode === 'amount'}
                  >
                    Total value
                  </button>
                  <button
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${displayMode === 'count' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                    onClick={() => setDisplayMode('count')}
                    aria-pressed={displayMode === 'count'}
                  >
                    Number of tips
                  </button>
                </div>
              </div>

              {/* Method row */}
              <div className="text-right pr-4 flex items-center justify-end h-full">
                <label htmlFor="method-select" className="text-sm text-gray-600 font-medium">Method:</label>
              </div>
              <div className="col-span-2">
                <select
                  id="method-select"
                  className="bg-white border border-gray-300 rounded-md text-sm py-1 px-2"
                  value={selectedMethod}
                  onChange={(e) => setSelectedMethod(e.target.value)}
                >
                  <option value="all">All methods</option>
                  <option value="page">Page</option>
                  <option value="button">Button</option>
                  <option value="qrCode">QR code</option>
                </select>
              </div>

              {/* Source row */}
              <div className="text-right pr-4 flex items-center justify-end h-full">
                <label htmlFor="source-select" className="text-sm text-gray-600 font-medium">Source:</label>
              </div>
              <div className="col-span-2">
                <select
                  id="source-select"
                  className="bg-white border border-gray-300 rounded-md text-sm py-1 px-2 max-w-xs"
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                >
                  <option value="all">All sources</option>
                  {availableSources.map((source, index) => (
                    <option key={index} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(filteredData.length !== 0) && (<div className="mt-8">
        <h3 id="table-caption" className="text-lg font-medium text-gray-800 mb-3">Top {groupBy === 'source' ? 'sources' : 'methods'}</h3>
        <div className="overflow-x-auto" tabIndex="0">
          <table className="min-w-full bg-white border border-gray-200" aria-labelledby="table-caption">
            <thead>
              <tr className="bg-gray-100">
                <th scope="col" className="py-2 px-4 border-b text-left">{groupBy === 'source' ? 'Source' : 'Method'}</th>
                <th scope="col" className="py-2 px-4 border-b text-right">Amount</th>
                <th scope="col" className="py-2 px-4 border-b text-right">Tips</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.sort((a, b) => displayMode === 'amount' ? 
                b.totalAmount - a.totalAmount : 
                b.count - a.count
              ).slice(0, 5).map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="py-2 px-4 border-b">
                    {groupBy === 'source' ? (<div className="flex items-center">
                      {item.favicon && (
                        <img
                          src={item.favicon}
                          alt={`${item.title || item.url} favicon`}
                          className="w-4 h-4 mr-2"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <div>
                        <div className="font-medium">{item.title || item.url}</div>
                        <div className="text-xs text-gray-500">{item.url}</div>
                      </div>
                    </div>) : item.methods ? (
                      item.methods.map((method, methodIndex) => (
                        <MethodBadge key={methodIndex} method={method} />
                      ))
                    ) : (
                      item.method && (
                        <MethodBadge method={item.method} />
                      )
                    )}
                  </td>
                  <td className="py-2 px-4 border-b text-right">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: item.currency || 'USD',
                      minimumFractionDigits: 2
                    }).format(item.totalAmount / 100)} {/* Assuming amount is in cents */}
                  </td>
                  <td className="py-2 px-4 border-b text-right">
                    {item.count}
                  </td>
                </tr>
              ))}

            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}

export default LocationPieChart;
